import React, { useState, useRef, useEffect } from "react";
import "./chat-popover.scss";
import Comment, { CommentType } from "./comment";
import CommentEditor, { CommentAuthor } from "./comment-editor";
import { Close } from "@mui/icons-material";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

interface ChatPopoverProps {
  readonly comments: CommentType[];
  readonly onCommentSubmit: (
    html: string,
    parentId: string | null,
    attachments: string[],
  ) => void;
  readonly onClose: () => void;
  readonly canWrite?: boolean;
}

interface ReplyingTo {
  author: CommentAuthor;
  parentId: string | null;
}

/**
 * ChatPopover displays a threaded comments panel with a rich editor.
 * It handles reply context and emits onCommentSubmit with html, parentId, attachments.
 */
export default function ChatPopover({
  comments,
  onCommentSubmit,
  onClose,
  canWrite,
}: ChatPopoverProps) {
  const [replyingTo, setReplyingTo] = useState<ReplyingTo | null>(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const commentsEndRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [comments]);

  const handleReplyClick = (author: CommentAuthor, parentId: string | null) => {
    setReplyingTo({ author, parentId });
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleSubmit = (html: string, attachments: string[]) => {
    const parentId = replyingTo?.parentId || null;
    onCommentSubmit(html, parentId, attachments);
    setReplyingTo(null);
  };

  return (
    <div className="chat-popover">
      <div className="chat-popover__caret" />
      <header className="chat-popover__header">
        <h2 className="chat-popover__title" style={{ marginBottom: 0 }}>
          Comments
        </h2>
        <button className="chat-popover__close-btn" onClick={onClose}>
          <Close />
        </button>
      </header>
      <OverlayScrollbarsComponent
        className="chat-popover__scroll"
        options={{
          overflow: { x: "hidden", y: "scroll" },
          scrollbars: {
            theme: "os-theme-dark",
            autoHide: "scroll",
            autoHideDelay: 400,
          },
        }}
      >
        <div className="chat-popover__comments">
          {comments.length > 0 ? (
            comments.map((comment) => (
              <Comment
                key={comment.id}
                comment={comment}
                onReply={handleReplyClick}
                canWrite={canWrite}
              />
            ))
          ) : (
            <span className="chat-popover__no-comments">No comments yet</span>
          )}
          <div ref={commentsEndRef} />
        </div>
      </OverlayScrollbarsComponent>

      {canWrite && (
        <div className="chat-popover__editor">
          <CommentEditor
            ref={editorRef}
            onSubmit={handleSubmit}
            replyingTo={replyingTo ? replyingTo.author : null}
            onCancelReply={handleCancelReply}
          />
        </div>
      )}
    </div>
  );
}
